import { useState, useEffect } from "react";
import axios from "../customAxios.js";
import {
  Alert,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import es from "date-fns/locale/es";
import CustomTableNew from "../components/CustomTableNew.jsx";
import { dateToDatabase } from "../utils/functions";
import { useAuth } from "../hooks/useAuth.jsx";

function PendingOrders(props) {
  //values to control pagination of the component table
  const [page, setPage] = useState(
    localStorage.getItem("")
      ? JSON.parse(localStorage.getItem("pendingOrdersFilter")).page
      : 0
  );
  const [rowsPerPage, setRowsPerPage] = useState(
    localStorage.getItem("pendingOrdersFilter")
      ? JSON.parse(localStorage.getItem("pendingOrdersFilter")).rowsPerPage
      : 20
  );

  //data that will be set on every page change and filter set
  const [pageData, setPageData] = useState();
  const [loadingPageData, setLoadingPageData] = useState(true);

  // const [loading, setLoading] = useState(false);
  const daysOffset = props.daysOffset;

  const [servicesTypes, setServicesTypes] = useState([]);

  //filter values of the table
  const [startDate, setStartDate] = useState(
    localStorage.getItem("pendingOrdersFilter")
      ? new Date(
          JSON.parse(localStorage.getItem("pendingOrdersFilter")).startDate
        )
      : new Date(new Date().setDate(new Date().getDate() - daysOffset))
  );
  const [endDate, setEndDate] = useState(
    localStorage.getItem("pendingOrdersFilter")
      ? new Date(
          JSON.parse(localStorage.getItem("pendingOrdersFilter")).endDate
        )
      : new Date()
  );
  const [serviceFilter, setServiceFilter] = useState(
    localStorage.getItem("pendingOrdersFilter")
      ? JSON.parse(localStorage.getItem("pendingOrdersFilter")).serviceFilter
      : "all"
  );
  const [search, setSearch] = useState(
    localStorage.getItem("pendingOrdersFilter")
      ? JSON.parse(localStorage.getItem("pendingOrdersFilter")).search
      : ""
  );

  //aux values for delay on text search fields
  const [auxSearcher, setAuxSearcher] = useState(
    localStorage.getItem("pendingOrdersFilter")
      ? JSON.parse(localStorage.getItem("pendingOrdersFilter")).search
      : ""
  );

  const [counters, setCounters] = useState({});
  const [counterLabels, setCounterLabels] = useState();
  // const [dateRefresh, setDateRefresh] = useState(false);

  const [errorOnRetrieveData, setErrorOnRetrieveData] = useState();
  const [comunas, setComunas] = useState([]);
  const [isInitialRender, setIsInitialRender] = useState(true);

  //useState to save user type
  const [userType, setUserType] = useState(props.mode);

  // get user profile:
  const { user } = useAuth();
  const isOperator = user.user_type.includes("operator");
  const is_staff = user.is_staff;
  const isSeller = user.user_type.includes("seller");
  const isAdmin = user.user_type.includes("admin");

  //useState to catch when general data is already fetched
  const [generalParamsFetched, setGeneralParamsFetched] = useState(false);

  //useState to count the number of orders filtered by backend
  const [ordersCount, setOrdersCount] = useState(0);

  useEffect(() => {
    //if localStorage "pendingOrdersFilter" does not exist, set page to 0
    if (!localStorage.getItem("pendingOrdersFilter")) {
      setPage(0);
    }
    //if is on useEffect not on initial render, set page 0
    if (isInitialRender) {
      setIsInitialRender(false);
    } else {
      setPage(0);
    }
  }, [startDate, endDate, serviceFilter, search, rowsPerPage]);

  useEffect(() => {
    if (!loadingPageData) {
      setLoadingPageData(true);
    }
    fetchOrders();
    //save on localStorage the filter and page values of orders
    localStorage.setItem(
      "pendingOrdersFilter",
      JSON.stringify({
        startDate,
        endDate,
        serviceFilter,
        search,
        page,
        rowsPerPage,
      })
    );
  }, [startDate, endDate, serviceFilter, search, rowsPerPage, page]);

  //useEffect to catch auxSearcher changes, and triger a function every 2 seconds that dont change
  useEffect(() => {
    // Delayed search function
    const delayedSearch = (value) => {
      // Your search logic goes here
      setSearch(value);
    };
    if (!loadingPageData) {
      setLoadingPageData(true);
    }
    // Timer variable for setTimeout
    let timer;

    // Set a delay of 2 seconds before executing the search
    const delay = 1500;

    // Clear the previous timer when auxSearcher changes
    clearTimeout(timer);

    // Set a new timer for delayed search
    timer = setTimeout(() => {
      delayedSearch(auxSearcher);
    }, delay);

    // Clean up the timer on component unmount or when auxSearcher changes
    return () => {
      clearTimeout(timer);
    };
  }, [auxSearcher]);

  const searchParams = () => {
    return {
      start_date: dateToDatabase(startDate),
      end_date: dateToDatabase(endDate),
      order_type: serviceFilter,
      order_status: "in_preparation",
      search: search,
    };
  };

  //function to fetch all general parameters that do not depends on the page and rowsPerPage
  async function fetchGeneralParams() {
    const url = isSeller
      ? "general_params/filtered_orders_seller_new/"
      : "general_params/filtered_orders_new/";

    try {
      const {
        data: status_counters,
        order_types,
        status_choices,
      } = await axios.get(url, { params: searchParams() });
      setCounters(status_counters);
      setOrdersCount(status_counters.total_orders);
      setServicesTypes(order_types);
      setCounterLabels(status_choices);
      setErrorOnRetrieveData();
      setGeneralParamsFetched(true);
    } catch (error) {
      console.error(
        `Ocurrió un error al obtener los contadores y opciones de filtro: ${error}`
      );
      setErrorOnRetrieveData(
        "Ocurrió un error al obtener los contadores y opciones de filtro"
      );
    }
  }

  const fetchOrders = async () => {
    const search_params_fetch_orders = {
      start_date: dateToDatabase(startDate),
      end_date: dateToDatabase(endDate),
      order_type: serviceFilter,
      search: search,
      page: page,
      rowsPerPage: rowsPerPage,
      order_status: "in_preparation",
    };

    const url = isSeller
      ? "filtered_orders_seller_new/"
      : "filtered_orders_new/";
    setLoadingPageData(true);
    try {
      const { data: orders } = await axios.get(url, {
        params: search_params_fetch_orders,
      });
      setPageData(orders);
      setErrorOnRetrieveData();
    } catch (error) {
      console.error(`Ocurrió un error al obtener las órdenes: ${error}`);
      setPageData([]);
      setErrorOnRetrieveData("Ocurrió un error al obtener las órdenes");
    } finally {
      setLoadingPageData(false);
    }
  };

  useEffect(() => {
    fetchGeneralParams();
  }, [startDate, endDate, serviceFilter, search]);

  const headerTable = [
    { columnName: "ID Referencia", dbName: "reference_id", enabled: true },
    {
      columnName: "Nombre Seller",
      dbName: "seller_company_social_reason",
      enabled: true,
    },
    { columnName: "Destinatario", dbName: "recipient", enabled: true },
    { columnName: "Fecha de Ingreso", dbName: "creation_date", enabled: true },
    { columnName: "Tipo de orden", dbName: "order_type", enabled: true },
    {
      columnName: "Cantidad de Existencias",
      dbName: "existences_quantity",
      enabled: true,
    },
    { columnName: "Estado", dbName: "fulfillment_status", enabled: true },
    { columnName: "Procesar", dbName: "process", enabled: true },
  ];
  return (
    <>
      <Grid item xs={12}>
        <h1>Preparación de Pedidos</h1>
      </Grid>

      <Grid item xs={12} sm className="flex-left filters-container">
        <div className="row-container request-search-container">
          <Grid container>
            <Grid item xs={6} sm="auto" className="input-date-filter">
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={es}
              >
                <DatePicker
                  label="Fecha de inicio"
                  value={startDate}
                  onChange={(newValue) => {
                    if (endDate < newValue) {
                      let newEndDate = new Date(newValue);
                      newEndDate.setDate(newEndDate.getDate() + daysOffset);
                      setEndDate(newEndDate);
                    }
                    setStartDate(newValue);
                  }}
                  className="mg-r-10"
                />
              </LocalizationProvider>
            </Grid>
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={es}
            >
              <DatePicker
                label="Fecha de fin"
                value={endDate}
                onChange={(newValue) => {
                  if (newValue < startDate) {
                    let newStartDate = new Date(newValue);
                    newStartDate.setDate(newStartDate.getDate() - daysOffset);
                    setStartDate(newStartDate);
                  }
                  setEndDate(newValue);
                }}
                disabled={startDate === null}
                className="mg-r-10 mg-l-10"
              />
            </LocalizationProvider>
            <Grid item xs={6} sm="auto" className="input-service-filter">
              <FormControl fullWidth variant="outlined">
                <InputLabel htmlFor="select-service-filte">
                  Tipo de servicio
                </InputLabel>
                <Select
                  value={serviceFilter}
                  onChange={(e) => {
                    setServiceFilter(e.target.value);
                  }}
                  input={<OutlinedInput id="select-service-filter" />}
                  IconComponent={() => (
                    <i className="material-icons-outlined">expand_more</i>
                  )}
                >
                  <MenuItem key={"all"} value={"all"}>
                    Todos
                  </MenuItem>
                  {Object.entries(servicesTypes).map(([kind, label], index) => (
                    <MenuItem key={index} value={kind}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <TextField
              variant="outlined"
              label="Buscar"
              name="search"
              value={auxSearcher}
              onChange={(e) => {
                setAuxSearcher(e.target.value);
              }}
              className="mg-l-10"
              disabled={false}
            />
          </Grid>
        </div>
      </Grid>

      <Grid container className="custom-table-container">
        <Grid item xs={12} className="flex-center mg-t-0">
          <>
            {errorOnRetrieveData ? (
              <Alert severity="error">{errorOnRetrieveData}</Alert>
            ) : (
              <CustomTableNew
                searchParams={searchParams}
                search={search}
                filterByDate={startDate !== null && endDate !== null}
                headerTable={headerTable}
                selected={"none"}
                setSelected={"none"}
                openCollapseButton={true}
                openCollapseTooltip={"Ver más"}
                openCollapseIcon={
                  <i className="material-icons-outlined icon-actions-panel-test">
                    add_circle_outline
                  </i>
                }
                collapseType={"pending-orders"}
                hasPagination={true}
                orderTypes={servicesTypes}
                editable={true}
                filterStates={{ startDate, endDate, serviceFilter, search }}
                totalCount={ordersCount}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                pageData={pageData}
                setPageData={setPageData}
                loadingPageData={loadingPageData}
              />
            )}
          </>
        </Grid>
      </Grid>
    </>
  );
}

export default PendingOrders;
